import { useRef, useState } from 'react';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/user-avatar';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { toast } from '@/hooks/useToast';
import { deleteUserAvatar, uploadUserAvatar } from '@/utils/imageStorage';

/** Formati accettati e tetto di dimensione: una foto profilo non ha motivo di pesare di piu'. */
const ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const MAX_SIZE_MB = 5;

/**
 * Foto profilo dell'utente corrente.
 *
 * Non usa `ImageUpload` (il dropzone da 350px degli itinerari): qui l'anteprima e' il
 * pallino tondo che si vedra' ovunque nell'app, e mostrarla nella sua forma finale e'
 * meta' dell'informazione. Senza foto resta il cerchio con le iniziali, che e' un
 * risultato valido e non un vuoto da riempire.
 *
 * Il file sta in `users/{id}/avatar.{ext}` (stessa cartella di `logo.png`) e l'URL
 * finisce in `users.avatar_url` via RPC: storage e database vanno sempre aggiornati
 * insieme, altrimenti resta un file che nessuno mostra o un URL che non risolve.
 */
export const AvatarUploader = () => {
    const { t } = useI18n();
    const { userData, refreshUserData } = useAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isBusy, setIsBusy] = useState(false);

    const avatarUrl = userData?.avatar_url ?? null;

    /** Scrive l'URL (o NULL) su users.avatar_url e riallinea il contesto auth. */
    const persist = async (url: string | null) => {
        const { error } = await supabase.rpc('update_my_avatar_url' as never, {
            p_avatar_url: url,
        } as never);
        if (error) throw new Error(error.message);
        await refreshUserData();
    };

    const handleFile = async (file: File) => {
        if (!userData?.id) return;

        if (!ACCEPTED.includes(file.type)) {
            toast({ title: t('profile.avatar.invalidFormat'), variant: 'destructive' });
            return;
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            toast({
                title: t('profile.avatar.tooLarge', { size: MAX_SIZE_MB }),
                variant: 'destructive',
            });
            return;
        }

        setIsBusy(true);
        try {
            const url = await uploadUserAvatar(userData.id, file);
            await persist(url);
            toast({ variant: 'success', title: t('profile.avatar.updated') });
        } catch (err) {
            console.error('Error uploading avatar:', err);
            toast({ title: t('profile.avatar.uploadError'), variant: 'destructive' });
        } finally {
            setIsBusy(false);
        }
    };

    const handleRemove = async () => {
        if (!userData?.id) return;
        setIsBusy(true);
        try {
            // Prima il database: se cade lo storage restiamo con un file orfano, mentre
            // l'ordine opposto lascerebbe a schermo un avatar che non esiste piu'.
            await persist(null);
            await deleteUserAvatar(userData.id);
            toast({ variant: 'success', title: t('profile.avatar.removed') });
        } catch (err) {
            console.error('Error removing avatar:', err);
            toast({ title: t('profile.avatar.removeError'), variant: 'destructive' });
        } finally {
            setIsBusy(false);
        }
    };

    return (
        <div className="flex items-center gap-4">
            <div className="relative">
                <UserAvatar
                    firstName={userData?.first_name}
                    lastName={userData?.last_name}
                    email={userData?.email}
                    imageUrl={avatarUrl}
                    size="xl"
                />
                {isBusy && (
                    <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                        <Loader2 className="h-5 w-5 animate-spin text-white" />
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <Upload className="mr-1.5 h-4 w-4" />
                        {avatarUrl ? t('profile.avatar.replace') : t('profile.avatar.upload')}
                    </Button>
                    {avatarUrl && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 hover:text-red-600"
                            disabled={isBusy}
                            onClick={handleRemove}
                        >
                            <Trash2 className="mr-1.5 h-4 w-4" />
                            {t('common.delete')}
                        </Button>
                    )}
                </div>
                <p className="text-xs text-gray-500">
                    {t('profile.avatar.hint', { size: MAX_SIZE_MB })}
                </p>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED.join(',')}
                className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Azzerare il value permette di riselezionare lo stesso file dopo
                    // un errore: senza, il change non scatta una seconda volta.
                    e.target.value = '';
                    if (file) void handleFile(file);
                }}
            />
        </div>
    );
};

export default AvatarUploader;
